// Post detail queries — polling has been removed in favor of push
// notifications (see PushNotificationFeedBridge). refetchOnWindowFocus
// keeps these queries fresh for users who have denied notification
// permission and as a defensive fallback if a push is missed.
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';

const REGENERATE_POLL_TIMEOUT = 2 * 60 * 1000; // 2 minutes — kept as a hard ceiling on the loader UI

/**
 * Simple non-polling query to fetch a single post
 * @param {string} postId - Post ID to fetch
 * @param {boolean} enabled - Whether query should be enabled
 */
export const useGetPostById = (postId, enabled = true) => {
  return useQuery({
    queryKey: ['post-details', postId],
    queryFn: async () => {
      const result = await api.getPostDetails(postId);
      return result;
    },
    enabled: enabled && !!postId,
    staleTime: 1000,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Poll for posts generation status with 2-minute timeout
 * @param {string} postId - Post ID to poll
 * @param {boolean} enabled - Whether polling should be active
 */
export const usePostByIdPoll = (postId, enabled) => {
  const startTimeRef = React.useRef(null);

  // Reset timer when query is disabled (between regenerations)
  React.useEffect(() => {
    if (!enabled) {
      startTimeRef.current = null;
    }
  }, [enabled]);

  return useQuery({
    queryKey: ['post-details-poll', postId],
    queryFn: async () => {
      // Initialize start time on first poll
      if (!startTimeRef.current) {
        startTimeRef.current = Date.now();
      }

      const result = await api.getPostDetails(postId);

      // Check if polling has exceeded timeout
      const elapsedTime = Date.now() - startTimeRef.current;
      if (
        elapsedTime > REGENERATE_POLL_TIMEOUT &&
        (result?.status === 'regeneration' || result?.status === 'regenerating')
      ) {
        const error = new Error(
          'Post regeneration is taking longer than expected. Please try again or contact support.'
        );
        error.code = 'REGENERATE_TIMEOUT';
        error.keepLoader = true; // Signal to keep loader active
        throw error;
      }

      return result;
    },
    enabled: enabled && !!postId,
    // Refetch is now triggered by push notifications via
    // PushNotificationFeedBridge.invalidateForPayload. Window focus is
    // kept as a defensive fallback for permission-denied users.
    refetchInterval: false,
    refetchOnWindowFocus: true,
    retry: 3,
    gcTime: 0,
    staleTime: 0,
    select: (data) => {
      if (data?.status === 'failed') {
        const error = new Error('Post generation failed');
        error.code = 'POST_FAILED';
        throw error;
      }
      return data;
    },
  });
};

/**
 * Dashboard posts query. Live updates come from FCM "post" pushes via
 * PushNotificationFeedBridge invalidating ['posts']; this query itself
 * does no polling.
 * @param {boolean} enabled - Whether query should be enabled
 * @param {function} onSuccess - Callback when posts are fetched successfully
 * @param {function} onError - Callback when fetching posts fails
 */
export const useGetPosts = (enabled, onSuccess, onError) => {
  return useQuery({
    queryKey: ['posts'],
    queryFn: async () => await api.checkPostsReady(),
    enabled: enabled,
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      console.error('Posts fetch error:', error);
      if (onError) onError(error);
    },
    select: (data) => data.filter((post) => post.status !== 'failed') || [], // Filter out failed posts from the main posts list
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};
